/**
 * Surveys List Page - View and manage all supplier surveys
 * 
 * Connected to backend APIs:
 * - GET /api/v1/supplier-portal/surveys
 * - POST /api/v1/supplier-portal/surveys
 * - POST /api/v1/supplier-portal/surveys/:id/launch
 * - POST /api/v1/supplier-portal/surveys/:id/close
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ClipboardList,
  Plus,
  Search,
  Filter,
  Eye,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  ChevronRight,
  Calendar,
  Users,
  MoreVertical,
  Rocket,
  XCircle,
  BarChart3,
  ArrowLeft,
  Globe
} from 'lucide-react';
import { apiFetch } from '../../../services/api_service';

const STATUS_CONFIG = {
  draft: { label: 'Draft', bg: 'bg-amber-500/20', text: 'text-amber-400', dotColor: 'bg-amber-400' },
  active: { label: 'Active', bg: 'bg-emerald-500/20', text: 'text-emerald-400', dotColor: 'bg-emerald-400' },
  closed: { label: 'Closed', bg: 'bg-[#2F2F2F]', text: 'text-[#6B6B6B]', dotColor: 'bg-[#6B6B6B]' }
};

export default function SurveysListPage() {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    fetchSurveys();
  }, [statusFilter, pagination.page]);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      setError(null);
      let url = `/supplier-portal/surveys?page=${pagination.page}&limit=${pagination.limit}`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }

      const data = await apiFetch(url);
      if (data?._error) throw new Error(data.message || 'Failed to fetch surveys');
      setSurveys(data.surveys || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (err) {
      console.error('Error fetching surveys:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLaunchSurvey = async (surveyId) => {
    try {
      const data = await apiFetch(`/supplier-portal/surveys/${surveyId}/launch`, { method: 'POST' });
      if (data?._error) throw new Error(data.message || 'Failed to launch survey');
      fetchSurveys();
      setActiveDropdown(null);
    } catch (err) {
      console.error('Error launching survey:', err);
      alert(err.message);
    }
  };

  const handleCloseSurvey = async (surveyId) => {
    try {
      const data = await apiFetch(`/supplier-portal/surveys/${surveyId}/close`, { method: 'POST' });
      if (data?._error) throw new Error(data.message || 'Failed to close survey');
      fetchSurveys();
      setActiveDropdown(null);
    } catch (err) {
      console.error('Error closing survey:', err);
      alert(err.message);
    }
  };

  // Filter surveys by search
  const filteredSurveys = surveys.filter(survey => 
    survey.surveyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    survey.surveyId?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate status for filtering
  const getSurveyStatus = (survey) => {
    if (survey.timeline?.closedAt) return 'closed';
    if (survey.timeline?.launchedAt) return 'active';
    return 'draft';
  };

  return (
    <div className="space-y-6" data-testid="surveys-list-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-[#6B6B6B] text-sm mb-2">
            <Link to="/value-chain/supplier-portal" className="hover:text-purple-400 flex items-center gap-1">
              <ArrowLeft size={14} />
              <Globe size={14} />
              <span>Supplier Portal</span>
            </Link>
            <ChevronRight size={14} />
            <span className="text-white">Surveys</span>
          </div>
          <h1 className="text-2xl font-bold text-white">All Surveys</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Manage supplier data collection surveys
          </p>
        </div>
        
        <button
          onClick={() => navigate('/value-chain/supplier-portal/surveys/new')}
          className="flex items-center gap-2 px-4 py-2.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 text-purple-400 rounded-lg transition-colors font-medium"
          data-testid="create-survey-btn"
        >
          <Plus size={18} />
          New Survey
        </button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard 
          label="Total Surveys" 
          value={pagination.total} 
          icon={ClipboardList}
          color="purple"
        />
        <StatCard 
          label="Active" 
          value={surveys.filter(s => getSurveyStatus(s) === 'active').length}
          icon={Send}
          color="emerald"
        />
        <StatCard 
          label="Draft" 
          value={surveys.filter(s => getSurveyStatus(s) === 'draft').length}
          icon={Clock}
          color="amber"
        />
        <StatCard 
          label="Closed" 
          value={surveys.filter(s => getSurveyStatus(s) === 'closed').length}
          icon={CheckCircle}
          color="slate"
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search surveys..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-purple-500/50 focus:outline-none"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#6B6B6B]" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-purple-500/50 focus:outline-none cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <button 
          onClick={fetchSurveys}
          className="p-2.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-[#6B6B6B] hover:text-white hover:border-purple-500/30 transition-colors"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
          <AlertCircle className="text-red-400 shrink-0" size={20} />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="text-purple-400 animate-spin" />
        </div>
      )}

      {/* Surveys Table */}
      {!loading && (
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#0A0A0A] border-b border-[#1F1F1F]">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">Survey</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">Progress</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">Due Date</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">Frameworks</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1F1F1F]">
              {filteredSurveys.map(survey => (
                <SurveyRow 
                  key={survey.id} 
                  survey={survey} 
                  isDropdownOpen={activeDropdown === survey.id}
                  onToggleDropdown={() => setActiveDropdown(activeDropdown === survey.id ? null : survey.id)}
                  onLaunch={() => handleLaunchSurvey(survey.id)}
                  onClose={() => handleCloseSurvey(survey.id)}
                />
              ))}
            </tbody>
          </table>

          {filteredSurveys.length === 0 && (
            <div className="p-12 text-center">
              <ClipboardList className="mx-auto text-[#2F2F2F] mb-4" size={48} />
              <h3 className="text-lg font-semibold text-white mb-2">No surveys found</h3>
              <p className="text-sm text-[#6B6B6B] mb-6">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first survey to get started'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <button 
                  onClick={() => navigate('/value-chain/supplier-portal/surveys/new')}
                  className="px-5 py-2.5 rounded-lg bg-purple-500/20 border border-purple-500/30 text-purple-400 font-medium hover:bg-purple-500/30 transition-colors"
                >
                  Create Survey
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#6B6B6B]">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <button 
              disabled={pagination.page === 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              className="px-3 py-1.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-[#A0A0A0] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-[#6B6B6B]">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button 
              disabled={pagination.page === pagination.pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              className="px-3 py-1.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-[#A0A0A0] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Sub-components
function StatCard({ label, value, icon: Icon, color }) {
  const colorClasses = {
    purple: 'bg-purple-500/20 text-purple-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    amber: 'bg-amber-500/20 text-amber-400',
    slate: 'bg-[#1F1F1F] text-[#6B6B6B]'
  };

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon size={18} />
        </div>
        <div>
          <div className="text-2xl font-bold text-white">{value}</div>
          <div className="text-xs text-[#6B6B6B]">{label}</div>
        </div>
      </div>
    </div>
  );
}

function SurveyRow({ survey, isDropdownOpen, onToggleDropdown, onLaunch, onClose }) {
  const navigate = useNavigate();
  
  // Determine status
  let status = 'draft';
  if (survey.timeline?.closedAt) status = 'closed';
  else if (survey.timeline?.launchedAt) status = 'active';
  
  const statusConfig = STATUS_CONFIG[status];
  
  // Calculate progress
  const totalRecipients = survey.progress?.totalRecipients || 0;
  const submitted = survey.progress?.surveysSubmitted || 0;
  const responseRate = totalRecipients > 0 ? Math.round((submitted / totalRecipients) * 100) : 0;
  
  // Due date
  const dueDate = survey.timeline?.dueDate ? new Date(survey.timeline.dueDate) : null;
  const daysUntilDue = dueDate ? Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const isOverdue = daysUntilDue !== null && daysUntilDue < 0 && status === 'active';

  return (
    <tr className="hover:bg-[#1F1F1F]/50 transition-colors">
      {/* Survey Info */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <ClipboardList className="text-purple-400" size={18} />
          </div>
          <div>
            <Link 
              to={`/value-chain/supplier-portal/surveys/${survey.id}`}
              className="text-sm font-medium text-white hover:text-purple-400 transition-colors"
            >
              {survey.surveyName}
            </Link>
            <p className="text-xs text-[#6B6B6B] font-mono">{survey.surveyId}</p>
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="px-5 py-4">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotColor}`} />
          {statusConfig.label}
          {isOverdue && (
            <span className="ml-1 px-1.5 py-0.5 rounded bg-red-500/30 text-red-400 text-[10px]">
              Overdue
            </span>
          )}
        </span>
      </td>

      {/* Progress */}
      <td className="px-5 py-4">
        <div className="w-32">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-[#6B6B6B]">{submitted}/{totalRecipients}</span>
            <span className={`font-medium ${
              responseRate >= 80 ? 'text-emerald-400' :
              responseRate >= 50 ? 'text-amber-400' :
              'text-red-400'
            }`}>{responseRate}%</span>
          </div>
          <div className="h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${
                responseRate >= 80 ? 'bg-emerald-500' :
                responseRate >= 50 ? 'bg-amber-500' :
                'bg-red-500'
              }`}
              style={{ width: `${responseRate}%` }}
            />
          </div>
        </div>
      </td>

      {/* Due Date */}
      <td className="px-5 py-4">
        {dueDate ? (
          <div className="flex items-center gap-2 text-sm">
            <Calendar size={14} className="text-[#6B6B6B]" />
            <span className={isOverdue ? 'text-red-400' : 'text-[#A0A0A0]'}>
              {dueDate.toLocaleDateString()}
            </span>
          </div>
        ) : (
          <span className="text-sm text-[#6B6B6B]">—</span>
        )}
      </td>

      {/* Frameworks */}
      <td className="px-5 py-4">
        <div className="flex flex-wrap gap-1">
          {(survey.frameworks || []).slice(0, 3).map((fw, idx) => (
            <span 
              key={idx}
              className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 text-[10px] border border-blue-500/30"
            >
              {fw}
            </span>
          ))}
          {(survey.frameworks || []).length > 3 && (
            <span className="px-2 py-0.5 rounded bg-[#1F1F1F] text-[#6B6B6B] text-[10px]">
              +{survey.frameworks.length - 3}
            </span>
          )}
        </div>
      </td>

      {/* Actions */}
      <td className="px-5 py-4 text-right">
        <div className="flex items-center justify-end gap-2 relative">
          <button
            onClick={() => navigate(`/value-chain/supplier-portal/surveys/${survey.id}`)}
            className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors"
            title="View Details"
          >
            <Eye size={16} />
          </button>
          
          <button
            onClick={() => navigate(`/value-chain/supplier-portal/surveys/${survey.id}/responses`)}
            className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors"
            title="View Responses"
          >
            <BarChart3 size={16} />
          </button>
          
          <div className="relative">
            <button
              onClick={onToggleDropdown}
              className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors"
            >
              <MoreVertical size={16} />
            </button>
            
            {isDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-[#111111] border border-[#1F1F1F] rounded-lg shadow-xl z-10">
                {status === 'draft' && (
                  <button
                    onClick={onLaunch}
                    className="w-full text-left px-4 py-2 text-sm text-emerald-400 hover:bg-[#1F1F1F] flex items-center gap-2"
                  >
                    <Rocket size={14} />
                    Launch Survey
                  </button>
                )}
                {status === 'active' && (
                  <button
                    onClick={onClose}
                    className="w-full text-left px-4 py-2 text-sm text-amber-400 hover:bg-[#1F1F1F] flex items-center gap-2"
                  >
                    <XCircle size={14} />
                    Close Survey
                  </button>
                )}
                <button
                  onClick={() => navigate(`/value-chain/supplier-portal/surveys/${survey.id}`)}
                  className="w-full text-left px-4 py-2 text-sm text-[#A0A0A0] hover:bg-[#1F1F1F] flex items-center gap-2"
                >
                  <Eye size={14} />
                  View Details
                </button>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}
